import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const cameraTypes = [
  {
    name: 'Bullet Cameras',
    description:
      'Long-range outdoor cameras with weatherproof housing, perfect for monitoring driveways, parking lots, and perimeters.',
    image:
      'https://public.readdy.ai/ai/img_res/f2d7dfca08dd100fc302891249eb2ab1.jpg',
    features: ['Outdoor', 'Long Range', 'Night Vision'],
  },
  {
    name: 'PTZ Cameras',
    description:
      'Low-profile eyeball-style cameras with a wide field of view and no IR reflection issues, ideal for retail stores, offices, and building entrances.',
    image:
      'https://readdy.ai/api/search-image?query=professional%20turret%20security%20camera%20white%20eyeball%20dome%20style%20housing%20modern%20surveillance%20equipment%20isolated%20on%20white%20background%20product%20photography%20clean%20minimal%20design&width=400&height=400&seq=turret2&orientation=squarish',
    features: ['Low Profile', 'Wide Angle', 'No IR Glare'],
  },
  {
    name: 'Turret Cameras',
    description:
      'Compact eyeball-style cameras with a ball-and-socket mount for flexible positioning, ideal for indoor and outdoor use with minimal IR reflection.',
    image:
      'https://public.readdy.ai/ai/img_res/edited_a8038d79edc7950a3a249f8b41a1ded8_ba4d756f.jpg',
    features: ['Low Profile', 'Flexible Mount', 'No IR Glare'],
  },
  {
    name: 'Dome Cameras',
    description:
      'Compact eyeball-style cameras with flexible positioning, great for corners, hallways, and tight spaces.',
    image:
      'https://readdy.ai/api/search-image?query=professional%20turret%20security%20camera%20white%20eyeball%20style%20housing%20modern%20surveillance%20equipment%20isolated%20on%20white%20background%20product%20photography%20clean%20design&width=400&height=400&seq=turret1&orientation=squarish',
    features: ['Compact', 'Flexible Mount', 'Easy Install'],
  },
];

const packages = [
  {
    name: 'Essential View',
    tier: 'STARTER',
    description: '2 cameras for small properties',
    color: 'bg-[#0A1628]',
    features: [
      '2 x 4mp or 8mp HD Cameras',
      '4-Channel NVR with 1TB Storage',
      'Night Vision up to 100ft',
      'Motion Detection & Alerts',
      'Mobile App Access',
      'Email & Push Notifications',
      'Professional Installation',
      'Remote Viewing',
      '3-Year Equipment Warranty',
    ],
    notIncluded: ['AI Person/Vehicle Detection', 'Cloud Storage'],
  },
  {
    name: 'Complete Coverage',
    tier: 'MOST POPULAR',
    description: '4 cameras for comprehensive protection',
    color: 'bg-[#D4A853]',
    featured: true,
    features: [
      '4 x4mp or 8mp ultra HD Cameras',
      '8-Channel NVR with 2TB Storage',
      'AI Person & Vehicle Detection',
      'Night Vision up to 130ft',
      'Smart Motion Zones',
      'Mobile App Control',
      'Remote Access',
      'Two-Way Audio (select models)',
      'Professional Installation',
      'Remote Viewing',
      '3-Year Equipment Warranty',
    ],
    notIncluded: [],
  },
  {
    name: 'Total Surveillance',
    tier: 'ULTIMATE',
    description: '8 cameras for maximum coverage',
    color: 'bg-[#0A1628]',
    features: [
      '8 x 4mp or 8mp ultra HD Cameras',
      '16-Channel NVR with 4TB Storage',
      'Advanced AI Detection',
      'Color Night Vision',
      'License Plate Recognition',
      'Smart Motion Zones',
      'Two-Way Audio',
      'Mobile App Control',
      'Professional Installation',
      '3-Year Equipment Warranty',
    ],
    notIncluded: [],
  },
];

const faqs = [
  {
    question: "What's the difference between 4K and 1080p cameras?",
    answer:
      '4K cameras provide 4 times the resolution of 1080p, capturing much finer detail. This means you can digitally zoom into footage and still see clear images—perfect for identifying faces, license plates, or other critical details. For most commercial and residential applications, we recommend 4K cameras for future-proof security.',
  },
  {
    question: 'How long does the NVR store footage?',
    answer:
      'Storage duration depends on the number of cameras, resolution settings, and hard drive size. With our standard packages: 2-camera systems typically store 30-45 days, 4-camera systems store 20-30 days, and 8-camera systems store 15-20 days. You can upgrade to larger hard drives for extended storage.',
  },
  {
    question: 'Can I view my cameras remotely?',
    answer:
      'Yes! All our camera systems include mobile app access for iOS and Android. You can view live feeds, playback recorded footage, receive motion alerts, and even control PTZ cameras from anywhere in the world with an internet connection.',
  },
  {
    question: 'Do cameras work at night?',
    answer:
      'Absolutely. All our cameras feature infrared night vision with ranges from 100-130 feet depending on the model. Premium cameras also offer color night vision using ambient light, providing full-color footage even in low-light conditions.',
  },
  {
    question: 'What is AI person and vehicle detection?',
    answer:
      'AI detection uses advanced algorithms to distinguish between people, vehicles, and other motion (like animals or tree branches). This dramatically reduces false alerts, so you only get notified when something important happens—not every time a leaf blows by.',
  },
  {
    question: 'Can I add more cameras later?',
    answer:
      'Yes! Our NVR systems are designed to scale. If you start with 4 cameras on an 8-channel NVR, you can easily add 4 more cameras later. We always recommend choosing an NVR with extra channels for future expansion.',
  },
  {
    question: 'What happens if my internet goes down?',
    answer:
      "Your cameras continue recording to the local NVR even without internet. You won't be able to view them remotely during the outage, but all footage is safely stored on the hard drive. Once internet is restored, remote access resumes immediately.",
  },
  {
    question: 'How long does installation take?',
    answer:
      'Most residential installations (2-4 cameras) are completed in 4-6 hours. Larger commercial systems (8+ cameras) typically take 1-2 days depending on the property size and cable routing complexity. We work efficiently to minimize disruption.',
  },
];

export default function CameraPackagesPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [selectedPackage, setSelectedPackage] = useState('');
  const [packageDropdownOpen, setPackageDropdownOpen] = useState(false);

  const packageOptions = [
    'Essential View (2 Cameras)',
    'Complete Coverage (4 Cameras)',
    'Total Surveillance (8 Cameras)',
    'Custom Camera Setup',
    'Not Sure — Need Advice',
  ];

  const scrollToContact = () => {
    const el = document.getElementById('camera-consultation-form');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = '/#contact';
    }
  };

  const handleFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    const form = e.currentTarget;
    const formData = new FormData(form);
    formData.set('package', selectedPackage);

    const textarea = form.querySelector('textarea');
    if (textarea && textarea.value.length > 500) {
      setFormStatus('error');
      return;
    }

    try {
      const urlEncoded = new URLSearchParams();
      formData.forEach((value, key) => {
        urlEncoded.append(key, value.toString());
      });

      const response = await fetch('https://readdy.ai/api/form/d6hp5p857726vudt81n0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setSelectedPackage('');
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  const cameraStructuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://alliancesecuritysystems.com/' },
        { '@type': 'ListItem', position: 2, name: 'Camera Packages', item: 'https://alliancesecuritysystems.com/camera-packages' },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': 'https://alliancesecuritysystems.com/camera-packages#service',
      name: 'Security Camera Installation',
      description: 'Professional HD and 4K security camera system installation with NVR recording, AI detection, night vision, and remote mobile access. Packages for 2, 4, or 8 cameras across the GTA.',
      provider: {
        '@type': 'LocalBusiness',
        name: 'Alliance Security Systems',
        telephone: '+1-888-458-9181',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '2355 Derry Road East, Unit 1',
          addressLocality: 'Mississauga',
          addressRegion: 'ON',
          postalCode: 'L5S 1V6',
          addressCountry: 'CA',
        },
      },
      areaServed: { '@type': 'AdministrativeArea', name: 'Greater Toronto Area' },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Security Camera Packages',
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Essential View — 2 Camera Package',
              description: '2 x HD cameras with 4-channel NVR, 1TB storage, night vision up to 100ft, motion detection, mobile app access, and professional installation.',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Complete Coverage — 4 Camera Package',
              description: '4 x Ultra HD cameras with 8-channel NVR, 2TB storage, AI person & vehicle detection, night vision up to 130ft, smart motion zones, and professional installation.',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Total Surveillance — 8 Camera Package',
              description: '8 x Ultra HD cameras with 16-channel NVR, 4TB storage, advanced AI detection, color night vision, license plate recognition, and professional installation.',
            },
          },
        ],
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '500',
        bestRating: '5',
      },
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="HD Security Camera Packages | 4K Surveillance Systems | Alliance Security Systems"
        description="Professional security camera packages with 4K resolution, AI detection, night vision & remote access. Choose from 2, 4, or 8 camera systems with NVR recording. Expert installation across GTA. Get your free camera assessment today."
        keywords="security cameras, 4K surveillance, HD camera systems, NVR recording, AI detection, night vision cameras, camera installation Toronto, video surveillance GTA"
        canonical="/camera-packages"
        structuredData={cameraStructuredData}
        faqItems={faqs}
      />

      {/* Navigation */}
      <nav className="bg-white shadow-md sticky top-0 z-50" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="flex flex-col leading-none">
              <span className="text-2xl sm:text-3xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
              <span className="text-[10px] sm:text-xs font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
            </div>
          </Link>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">
              Home
            </Link>
            <Link to="/alarm-packages" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">
              Alarm Packages
            </Link>
            <Link to="/access-control" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">
              Access Control
            </Link>
            <Link to="/clients" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">
              Clients
            </Link>
            <a
              href="tel:18884589181"
              className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line"></i>1-888-458-9181
            </a>
            <button
              onClick={scrollToContact}
              className="bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap"
            >
              Get Free Quote
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 pb-4 space-y-2">
            <Link to="/" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">
              Home
            </Link>
            <Link to="/alarm-packages" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">
              Alarm Packages
            </Link>
            <Link to="/access-control" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">
              Access Control
            </Link>
            <Link to="/clients" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">
              Clients
            </Link>
            <a href="tel:18884589181" className="flex items-center gap-2 py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">
              <i className="ri-phone-line"></i>1-888-458-9181
            </a>
            <button
              onClick={scrollToContact}
              className="w-full bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap mt-2"
            >
              Get Free Quote
            </button>
          </div>
        )}
      </nav>

      <main>
        {/* Hero Section */}
        <section className="relative py-14 sm:py-20 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=modern%20security%20camera%20system%20monitoring%20multiple%20screens%20in%20professional%20control%20room%20dark%20ambient%20lighting%20with%20blue%20accents%20high%20tech%20surveillance%20center%20wide%20angle%20view&width=1920&height=800&seq=camhero1&orientation=landscape"
              alt="Security Camera Control Room"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/95 via-[#0A1628]/80 to-[#0A1628]/60"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
            <nav className="flex items-center gap-2 text-xs sm:text-sm text-white/60 mb-6 sm:mb-8" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-white cursor-pointer">
                Home
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span>Services</span>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-[#D4A853]">Camera Packages</span>
            </nav>

            <div className="max-w-3xl">
              <span className="inline-block bg-[#D4A853] text-white px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6 whitespace-nowrap">
                HD SECURITY CAMERAS
              </span>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-4 sm:mb-6 leading-tight">
                Crystal Clear Protection, 24/7
              </h1>
              <p className="text-base sm:text-xl text-white/80 leading-relaxed mb-6 sm:mb-8">
                Professional-grade surveillance systems with 4K resolution, AI-powered detection, and remote access from anywhere in the world.
              </p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                <button
                  onClick={scrollToContact}
                  className="bg-[#D4A853] text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap shadow-lg text-center"
                >
                  Get Free Camera Assessment
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Camera Types Section */}
        <section className="py-12 sm:py-20 bg-gray-50" aria-labelledby="camera-types-heading">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-8 sm:mb-12">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                CAMERA OPTIONS
              </span>
              <h2 id="camera-types-heading" className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Choose the Right Camera Type
              </h2>
              <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">
                We offer a variety of camera styles to suit every monitoring need
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {cameraTypes.map((camera, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="relative w-full h-32 sm:h-48 bg-gray-100">
                    <img src={camera.image} alt={camera.name} className="w-full h-full object-contain object-center p-3 sm:p-4" />
                  </div>
                  <div className="p-4 sm:p-6">
                    <h3 className="text-base sm:text-xl font-bold text-[#0A1628] mb-1 sm:mb-2">{camera.name}</h3>
                    <p className="text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-3">{camera.description}</p>
                    <div className="flex flex-wrap gap-1.5 sm:gap-2">
                      {camera.features.map((feature, idx) => (
                        <span
                          key={idx}
                          className="bg-[#D4A853]/10 text-[#D4A853] px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium whitespace-nowrap"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Cards Section */}
        <section className="py-12 sm:py-20 bg-white" aria-labelledby="packages-heading">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-8 sm:mb-12">
              <span className="inline-block bg-[#0A1628]/10 text-[#0A1628] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                PRICING PLANS
              </span>
              <h2 id="packages-heading" className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Camera Packages for Every Need
              </h2>
              <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Professional installation included with all packages
              </p>
            </div>

            {/* Package Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 items-start">
              {packages.map((pkg, index) => (
                <div
                  key={index}
                  className={`relative bg-white rounded-2xl sm:rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden ${
                    pkg.featured ? 'ring-2 ring-[#D4A853] md:-mt-4 md:mb-4' : ''
                  }`}
                >
                  <div className={`h-2 ${pkg.color}`}></div>
                  {pkg.featured && (
                    <div className="absolute top-6 right-6 bg-[#D4A853] text-white px-4 py-1 rounded-full text-xs font-bold">
                      {pkg.tier}
                    </div>
                  )}
                  <div className="p-10">
                    {!pkg.featured && (
                      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{pkg.tier}</span>
                    )}
                    <h3 className="text-2xl sm:text-3xl font-bold text-[#0A1628] mt-2 mb-2">{pkg.name}</h3>
                    <p className="text-gray-500 text-xs sm:text-sm mb-4 sm:mb-6">{pkg.description}</p>
                    <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${pkg.featured ? 'bg-[#D4A853]' : 'bg-emerald-500'}`}>
                            <i className="ri-check-line text-white text-xs"></i>
                          </div>
                          <span className="text-gray-700 text-xs sm:text-sm">{feature}</span>
                        </li>
                      ))}
                      {pkg.notIncluded.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3 opacity-50">
                          <div className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-close-line text-white text-xs"></i>
                          </div>
                          <span className="text-gray-500 text-xs sm:text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={scrollToContact}
                      className={`w-full py-3.5 sm:py-4 rounded-xl font-semibold transition-all cursor-pointer whitespace-nowrap text-sm sm:text-base ${
                        pkg.featured
                          ? 'bg-[#D4A853] text-white hover:bg-[#B8923D] shadow-lg hover:shadow-xl'
                          : 'border-2 border-[#0A1628] text-[#0A1628] hover:bg-[#0A1628] hover:text-white'
                      }`}
                    >
                      Get Started
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* NVR Recording Section */}
        <section className="py-16 sm:py-24 bg-[#F7F5F0]" aria-labelledby="nvr-heading">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            {/* Section Header */}
            <div className="text-center mb-12 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-5 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                LOCAL RECORDING
              </span>
              <h2 id="nvr-heading" className="text-2xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Network Video Recorder (NVR)
              </h2>
              <p className="text-sm sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Every camera package includes a dedicated NVR unit for reliable, on-site recording — ensuring your footage is always secure, even without an internet connection.
              </p>
            </div>

            {/* Hero Block — Image + Text */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center mb-14 sm:mb-20">
              <div className="relative w-full h-[260px] sm:h-[380px] rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20network%20video%20recorder%20NVR%20device%20black%20metal%20chassis%20with%20LED%20indicators%20and%20multiple%20hard%20drive%20bays%20modern%20security%20recording%20equipment%20on%20clean%20dark%20surface%20studio%20product%20photography%20with%20dramatic%20lighting&width=800&height=600&seq=nvr1&orientation=landscape"
                  alt="Network Video Recorder NVR Device"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                <div className="absolute bottom-5 left-5 sm:bottom-6 sm:left-6">
                  <span className="bg-[#D4A853] text-white px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap">
                    Included in All Packages
                  </span>
                </div>
              </div>

              <div>
                <h3 className="text-xl sm:text-3xl font-bold text-[#0A1628] mb-4 sm:mb-6">
                  Your Footage, Stored Locally &amp; Securely
                </h3>
                <p className="text-sm sm:text-base text-gray-600 mb-5 sm:mb-6 leading-relaxed">
                  Our NVR systems record directly from your cameras to a local hard drive, giving you complete ownership of your surveillance data. Unlike cloud-only solutions, local NVR recording means no monthly storage fees, no bandwidth limitations, and no risk of losing footage due to internet outages.
                </p>
                <div className="space-y-3 sm:space-y-4">
                  {[
                    { icon: 'ri-shield-keyhole-line', text: 'Tamper-proof local storage with encrypted hard drives' },
                    { icon: 'ri-wifi-off-line', text: 'Continues recording even during internet outages' },
                    { icon: 'ri-money-dollar-circle-line', text: 'No recurring cloud storage fees for local recordings' },
                    { icon: 'ri-speed-line', text: 'Instant playback with no buffering or download delays' },
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 bg-[#D4A853]/15 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-2xl sm:text-xl text-[#D4A853]`}></i>
                      </div>
                      <span className="text-sm sm:text-base text-gray-700 pt-1.5">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* 3-Column Feature Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-8 mb-14 sm:mb-20">
              {[
                {
                  icon: 'ri-hard-drive-2-line',
                  title: 'High-Capacity Storage',
                  description:
                    'Supports up to 8TB hard drives, providing weeks or even months of continuous recording depending on camera count and resolution settings.',
                  highlights: ['Up to 8TB HDD', 'Auto-Overwrite Oldest', 'RAID Support'],
                },
                {
                  icon: 'ri-vidicon-line',
                  title: 'Multi-Channel Recording',
                  description:
                    'Record from multiple cameras simultaneously in full resolution. Our NVR units support 4 to 16 channels, scaling with your security needs.',
                  highlights: ['4 / 8 / 16 Channels', '4K per Channel', 'Simultaneous Recording'],
                },
                {
                  icon: 'ri-search-eye-line',
                  title: 'Smart Playback & Search',
                  description:
                    'Quickly find specific events with intelligent search by date, time, camera, or motion-triggered events. Export clips directly to USB.',
                  highlights: ['Timeline Search', 'Motion Event Filter', 'USB Export'],
                },
              ].map((card, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#0A1628] rounded-xl flex items-center justify-center mb-5 sm:mb-6">
                    <i className={`${card.icon} text-2xl sm:text-3xl text-[#D4A853]`}></i>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-[#0A1628] mb-2 sm:mb-3">{card.title}</h3>
                  <p className="text-sm text-gray-600 mb-5 leading-relaxed">{card.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {card.highlights.map((tag, idx) => (
                      <span key={idx} className="bg-[#0A1628]/5 text-[#0A1628] px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Technical Specs Bar */}
            <div className="bg-[#0A1628] rounded-2xl p-6 sm:p-10 overflow-hidden">
              <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6 lg:gap-0 lg:justify-between">
                <div className="lg:border-r lg:border-white/10 lg:pr-10">
                  <p className="text-[#D4A853] text-xs font-semibold uppercase tracking-wider mb-1">Video Input</p>
                  <p className="text-white text-lg sm:text-2xl font-bold">4 – 16 Channels</p>
                </div>
                <div className="lg:border-r lg:border-white/10 lg:px-10">
                  <p className="text-[#D4A853] text-xs font-semibold uppercase tracking-wider mb-1">Max Resolution</p>
                  <p className="text-white text-lg sm:text-2xl font-bold">4K Ultra HD</p>
                </div>
                <div className="lg:border-r lg:border-white/10 lg:px-10">
                  <p className="text-[#D4A853] text-xs font-semibold uppercase tracking-wider mb-1">Storage</p>
                  <p className="text-white text-lg sm:text-2xl font-bold">Up to 8TB HDD</p>
                </div>
                <div className="lg:border-r lg:border-white/10 lg:px-10">
                  <p className="text-[#D4A853] text-xs font-semibold uppercase tracking-wider mb-1">Network</p>
                  <p className="text-white text-lg sm:text-2xl font-bold">PoE Built-In</p>
                </div>
                <div className="lg:pl-10">
                  <p className="text-[#D4A853] text-xs font-semibold uppercase tracking-wider mb-1">Remote Access</p>
                  <p className="text-white text-lg sm:text-2xl font-bold">App + Web</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Installation Process Section */}
        <section className="py-16 sm:py-24 bg-white" aria-labelledby="installation-heading">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            {/* Section Header */}
            <div className="text-center mb-12 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-5 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                HOW IT WORKS
              </span>
              <h2 id="installation-heading" className="text-2xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Professional Camera Installation
              </h2>
              <p className="text-sm sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                From initial consultation to final walkthrough, our certified technicians handle every step to ensure your cameras are perfectly positioned and fully operational.
              </p>
            </div>

            {/* Steps */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-14 sm:mb-20">
              {[
                {
                  step: '01',
                  icon: 'ri-search-eye-line',
                  title: 'Free Site Assessment',
                  description:
                    'Our security expert visits your property to evaluate entry points, blind spots, and optimal camera placement for maximum coverage.',
                  details: ['Property walkthrough', 'Vulnerability analysis', 'Custom camera map'],
                },
                {
                  step: '02',
                  icon: 'ri-draft-line',
                  title: 'Custom System Design',
                  description:
                    'We design a tailored surveillance plan based on your property layout, selecting the right camera types and positions for complete protection.',
                  details: ['Camera type selection', 'Coverage blueprint', 'NVR sizing & config'],
                },
                {
                  step: '03',
                  icon: 'ri-tools-line',
                  title: 'Expert Installation',
                  description:
                    'Our licensed technicians install and wire all cameras, mount the NVR, and configure your entire system — typically completed in one visit.',
                  details: ['Clean cable routing', 'Weatherproof mounting', 'NVR & network setup'],
                },
                {
                  step: '04',
                  icon: 'ri-smartphone-line',
                  title: 'Setup & Training',
                  description:
                    'We configure remote access on your devices, fine-tune motion zones, and walk you through the app so you can monitor with confidence.',
                  details: ['Mobile app setup', 'Alert customization', 'Full system walkthrough'],
                },
              ].map((item, index) => (
                <div key={index} className="relative group">
                  {/* Connector line (hidden on mobile, shown on lg) */}
                  {index < 3 && (
                    <div className="hidden lg:block absolute top-12 left-[calc(100%+0.5rem)] w-[calc(100%-3rem)] h-[2px] bg-gradient-to-r from-[#D4A853]/40 to-[#D4A853]/10 z-0"></div>
                  )}
                  <div className="bg-[#F8F9FA] rounded-2xl p-6 sm:p-8 border border-gray-100 hover:border-[#D4A853]/30 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 relative z-10 h-full">
                    {/* Step number + icon */}
                    <div className="flex items-center gap-4 mb-5 sm:mb-6">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#D4A853] rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                        <i className={`${item.icon} text-2xl sm:text-3xl text-white`}></i>
                      </div>
                      <span className="text-3xl sm:text-4xl font-extrabold text-[#D4A853]/20">{item.step}</span>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-[#0A1628] mb-2 sm:mb-3">{item.title}</h3>
                    <p className="text-sm text-gray-600 mb-4 sm:mb-5 leading-relaxed">{item.description}</p>
                    <ul className="space-y-2">
                      {item.details.map((detail, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded-full bg-[#D4A853]/15 flex items-center justify-center flex-shrink-0">
                            <i className="ri-check-line text-[#D4A853] text-xs"></i>
                          </div>
                          <span className="text-sm text-gray-700">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom Assurance Bar */}
            <div className="bg-[#0A1628] rounded-2xl p-6 sm:p-10 overflow-hidden">
              <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-0 lg:justify-between">
                {[
                  { icon: 'ri-shield-check-line', label: 'Licensed Technicians', sublabel: 'Factory-certified & insured' },
                  { icon: 'ri-tools-line', label: 'Clean Installation', sublabel: 'Hidden cables & neat wiring' },
                  { icon: 'ri-customer-service-2-line', label: 'Post-Install Support', sublabel: 'Free 30-day follow-up' },
                ].map((item, index) => (
                  <div
                    key={index}
                    className={`flex items-center gap-3 sm:gap-4 ${index < 2 ? 'lg:border-r lg:border-white/10 lg:pr-8 xl:pr-10' : ''} ${
                      index > 0 ? 'lg:pl-8 xl:pl-10' : ''
                    }`}
                  >
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#D4A853]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-xl sm:text-2xl text-[#D4A853]`}></i>
                    </div>
                    <div>
                      <p className="text-white font-bold text-sm sm:text-base">{item.label}</p>
                      <p className="text-white/50 text-xs sm:text-sm">{item.sublabel}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Features Highlight */}
        <section className="py-12 sm:py-20 bg-[#0A1628]" aria-labelledby="ai-features-heading">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div>
                <span className="inline-block bg-[#D4A853] text-white px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6 whitespace-nowrap">
                  ADVANCED TECHNOLOGY
                </span>
                <h2 className="text-2xl sm:text-4xl font-bold text-white mb-4 sm:mb-6">
                  AI-Powered Smart Detection
                </h2>
                <p className="text-sm sm:text-lg text-white/70 mb-6 sm:mb-8 leading-relaxed">
                  Our cameras use advanced artificial intelligence to distinguish between people, vehicles, animals, and other motion—reducing false alerts and ensuring you're notified only when it matters.
                </p>

                <div className="space-y-4 sm:space-y-6">
                  {[
                    { icon: 'ri-user-search-line', title: 'Person Detection', desc: 'Instantly identify when a person enters the frame' },
                    { icon: 'ri-car-line', title: 'Vehicle Recognition', desc: 'Track cars and receive license plate alerts' },
                    { icon: 'ri-notification-badge-line', title: 'Smart Alerts', desc: 'Customizable notifications based on detection type' },
                    { icon: 'ri-map-pin-line', title: 'Activity Zones', desc: 'Define specific areas to monitor for motion' },
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3 sm:gap-4">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#D4A853]/20 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-xl sm:text-2xl text-[#D4A853]`}></i>
                      </div>
                      <div>
                        <h3 className="text-base sm:text-lg font-bold text-white mb-0.5 sm:mb-1">{item.title}</h3>
                        <p className="text-white/60 text-xs sm:text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                <div className="relative w-full h-[300px] sm:h-[500px] rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=smartphone%20displaying%20security%20camera%20app%20interface%20with%20multiple%20live%20camera%20feeds%20and%20AI%20detection%20alerts%20modern%20mobile%20application%20design%20professional%20mockup%20on%20dark%20background&width=800&height=1000&seq=camapp1&orientation=portrait"
                    alt="Security Camera App"
                    className="w-full h-full object-contain object-center"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 sm:py-24 bg-gray-50" aria-labelledby="faq-heading">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-8 sm:mb-12">
              <h2 id="faq-heading" className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">Frequently Asked Questions</h2>
              <p className="text-sm sm:text-lg text-gray-600">Everything you need to know about our camera systems</p>
            </div>

            <div className="space-y-3 sm:space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl sm:rounded-2xl border border-gray-200 overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-4 sm:p-6 text-left cursor-pointer"
                  >
                    <span className="font-semibold text-[#0A1628] pr-4 text-sm sm:text-base">{faq.question}</span>
                    <i
                      className={`ri-arrow-down-s-line text-xl sm:text-2xl text-[#D4A853] transition-transform flex-shrink-0 ${
                        openFaq === index ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                      <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Free Consultation Form Section */}
        <section id="camera-consultation-form" className="py-12 sm:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
              <div>
                <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-6 py-2 rounded-full text-sm font-semibold mb-6 whitespace-nowrap">
                  FREE CONSULTATION
                </span>
                <h2 className="text-3xl sm:text-5xl font-bold text-[#0A1628] mb-6 leading-tight">
                  Get a Free Camera System Quote
                </h2>
                <p className="text-lg text-gray-600 mb-10 leading-relaxed">
                  Not sure which camera package fits your property? Share your details and our surveillance experts will design the perfect system — completely free, no obligation.
                </p>

                <div className="space-y-6 mb-10">
                  {[
                    { icon: 'ri-timer-line', title: 'Fast Response', desc: "We'll get back to you within 2 business hours" },
                    { icon: 'ri-search-eye-line', title: 'Free Site Survey', desc: 'On-site walkthrough to map optimal camera positions' },
                    { icon: 'ri-price-tag-3-line', title: 'Transparent Pricing', desc: 'Detailed quote with no hidden fees or surprises' },
                    { icon: 'ri-shield-check-line', title: 'No Obligation', desc: 'Zero pressure — the consultation is completely free' },
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#D4A853]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-xl text-[#D4A853]`}></i>
                      </div>
                      <div>
                        <h4 className="text-base font-bold text-[#0A1628] mb-1">{item.title}</h4>
                        <p className="text-sm text-gray-500">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center gap-4 p-5 bg-[#0A1628] rounded-2xl">
                  <div className="w-12 h-12 bg-[#D4A853] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl text-white"></i>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs mb-0.5">Prefer to talk now?</p>
                    <a href="tel:18884589181" className="text-white font-bold text-lg hover:text-[#D4A853] transition-colors cursor-pointer">
                      1-888-458-9181
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-3xl p-8 sm:p-10 border border-gray-200">
                {formStatus === 'success' ? (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <i className="ri-check-line text-4xl text-green-600"></i>
                    </div>
                    <h3 className="text-2xl font-bold text-[#0A1628] mb-3">Request Received!</h3>
                    <p className="text-gray-600 mb-6 max-w-sm mx-auto">
                      Thank you for your interest. Our camera specialists will contact you within 2 business hours with a personalized quote.
                    </p>
                    <button
                      onClick={() => setFormStatus('idle')}
                      className="text-[#D4A853] font-semibold hover:underline cursor-pointer whitespace-nowrap"
                    >
                      Submit another request
                    </button>
                  </div>
                ) : (
                  <>
                    <h3 className="text-2xl font-bold text-[#0A1628] mb-2">Get Your Free Quote</h3>
                    <p className="text-sm text-gray-500 mb-8">Fill out the form below and we'll reach out shortly.</p>

                    <form id="camera-consultation" data-readdy-form onSubmit={handleFormSubmit} className="space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-semibold text-[#0A1628] mb-2">Full Name *</label>
                          <input
                            type="text"
                            name="fullName"
                            required
                            placeholder="John Smith"
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-[#0A1628] mb-2">Phone Number *</label>
                          <input
                            type="tel"
                            name="phone"
                            required
                            placeholder="(416) 555-0123"
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-[#0A1628] mb-2">Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          required
                          placeholder="john@example.com"
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all"
                        />
                      </div>

                      <div className="relative">
                        <label className="block text-sm font-semibold text-[#0A1628] mb-2">Interested Package *</label>
                        <input type="hidden" name="package" value={selectedPackage} />
                        <button
                          type="button"
                          onClick={() => setPackageDropdownOpen(!packageDropdownOpen)}
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all cursor-pointer"
                        >
                          <span className={selectedPackage ? 'text-[#0A1628]' : 'text-gray-400'}>
                            {selectedPackage || 'Select a package'}
                          </span>
                          <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${packageDropdownOpen ? 'rotate-180' : ''}`}></i>
                        </button>
                        {packageDropdownOpen && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 max-h-56 overflow-y-auto">
                            {packageOptions.map((option) => (
                              <button
                                key={option}
                                type="button"
                                onClick={() => {
                                  setSelectedPackage(option);
                                  setPackageDropdownOpen(false);
                                }}
                                className={`w-full text-left px-4 py-3 text-sm hover:bg-[#D4A853]/10 transition-colors cursor-pointer ${
                                  selectedPackage === option ? 'text-[#D4A853] font-semibold bg-[#D4A853]/5' : 'text-gray-700'
                                }`}
                              >
                                {option}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-semibold text-[#0A1628] mb-2">Property Type</label>
                          <input
                            type="text"
                            name="propertyType"
                            placeholder="e.g. Detached House, Warehouse"
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-[#0A1628] mb-2">Number of Cameras Needed</label>
                          <input
                            type="text"
                            name="cameraCount"
                            placeholder="e.g. 4 outdoor, 2 indoor"
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-[#0A1628] mb-2">Additional Details</label>
                        <textarea
                          name="details"
                          rows={4}
                          maxLength={500}
                          placeholder="Tell us about your property, areas you want covered, and any specific concerns..."
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all resize-none"
                        ></textarea>
                        <p className="text-xs text-gray-400 mt-1">Max 500 characters</p>
                      </div>

                      {formStatus === 'error' && (
                        <div className="flex items-center gap-2 text-red-600 bg-red-50 px-4 py-3 rounded-xl text-sm">
                          <i className="ri-error-warning-line text-lg"></i>
                          Something went wrong. Please try again or call us directly.
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={formStatus === 'submitting' || !selectedPackage}
                        className={`w-full py-3.5 sm:py-4 rounded-xl font-semibold transition-all cursor-pointer whitespace-nowrap text-sm sm:text-base ${
                          selectedPackage
                            ? 'bg-[#D4A853] text-white hover:bg-[#B8923D] shadow-lg hover:shadow-xl'
                            : 'border-2 border-[#0A1628] text-[#0A1628] hover:bg-[#0A1628] hover:text-white'
                        }`}
                      >
                        {formStatus === 'submitting' ? (
                          <>
                            <i className="ri-loader-4-line animate-spin text-lg"></i>
                            Submitting...
                          </>
                        ) : (
                          <>
                            Request Free Quote
                            <i className="ri-arrow-right-line text-lg"></i>
                          </>
                        )}
                      </button>

                      <p className="text-xs text-gray-400 text-center">
                        By submitting, you agree to our{' '}
                        <Link to="/privacy-policy" className="underline hover:text-[#D4A853] cursor-pointer">
                          Privacy Policy
                        </Link>
                        . We'll never share your information.
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-12 sm:py-20 bg-[#0A1628]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-2xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">Ready to See Everything, Everywhere?</h2>
            <p className="text-base sm:text-xl text-white/70 mb-6 sm:mb-8">
              Get a free camera assessment and custom quote for your property.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <button
                onClick={scrollToContact}
                className="bg-[#D4A853] text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap shadow-xl inline-flex items-center justify-center gap-3"
              >
                <i className="ri-camera-line text-xl"></i>
                Schedule Free Assessment
              </button>
              <a
                href="tel:18884589181"
                className="border-2 border-white text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-white hover:text-[#0A1628] transition-all cursor-pointer whitespace-nowrap inline-flex items-center justify-center gap-3"
              >
                <i className="ri-phone-line text-xl"></i>
                1-888-458-9181
              </a>
            </div>
            <p className="text-white/50 text-xs sm:text-sm mt-4">No obligation • Professional installation included</p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-[#050B14] text-white py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
            <Link to="/" className="flex items-center gap-3">
              <div className="flex flex-col leading-none">
                <span className="text-xl sm:text-2xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
                <span className="text-[8px] sm:text-[10px] font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
              </div>
            </Link>
            <p className="text-white/60 text-xs sm:text-sm">© 2024 Security Solutions. All rights reserved.</p>
            <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
